// =============================================================================
// supplier-announcements-client.http.ts — Adaptateur HTTP RÉEL du SupplierAnnouncementsClient (R15-COM-07).
// =============================================================================
// Câble la SPA contre les routes @SupplierRoute de la réception d'annonces :
//   GET   /api/supplier/announcements[?campaignId=…]        (onglet §3.9 — LECTURE, pas de CSRF)
//   GET   /api/supplier/announcements/:announcementId       (détail + content_rich — LECTURE)
//   PATCH /api/supplier/announcements/:announcementId/read  (accusé de lecture — MUTATION ⇒ CSRF)
//
// SÉCURITÉ : session fournisseur = COOKIE httpOnly `supplier_session` (credentials: 'include'). La MUTATION
// (PATCH) exige le double-submit CSRF — on ré-émet le cookie NON-httpOnly `supplier_csrf` en en-tête
// `x-csrf-token` (parité notifications-client.http). AUCUNE donnée métier en localStorage (RULE_REF_006).
// MAPPING ERREURS par STATUS HTTP (401/403/404), message serveur relayé.

import {
  SupplierAnnouncementsApiError,
  type SupplierAnnouncementsApiErrorKind,
  type SupplierAnnouncementsClient,
  type SupplierAnnouncementDetailDto,
  type SupplierAnnouncementListDto,
  type SupplierAnnouncementReadAckDto,
} from '@/api/supplier-announcements-client';

/** Forme du corps d'erreur NestJS (HttpException standard) — message éventuellement string[]. */
interface NestErrorBody {
  readonly statusCode?: number;
  readonly message?: string | readonly string[];
  readonly error?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login FOURNISSEUR (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'supplier_csrf';

/** Mapping STATUS HTTP → kind UX (401 session, 403 garde, 404 ressource). */
const STATUS_TO_KIND: Readonly<Record<number, SupplierAnnouncementsApiErrorKind>> = {
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
};

/** Lit la valeur du cookie CSRF `supplier_csrf` (NON-httpOnly). `undefined` si absent (fail-closed serveur). */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : appels `fetch` contre l'API réception d'annonces. `baseUrl` par défaut `/api`
 * (la SPA et l'API partagent l'origine, ou un reverse proxy route `/api/*`).
 */
export class HttpSupplierAnnouncementsClient implements SupplierAnnouncementsClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async list(campaignId?: string): Promise<SupplierAnnouncementListDto> {
    const query =
      campaignId !== undefined && campaignId.length > 0
        ? `?campaignId=${encodeURIComponent(campaignId)}`
        : '';
    const response = await fetch(`${this.baseUrl}/supplier/announcements${query}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as SupplierAnnouncementListDto;
  }

  public async getDetail(announcementId: string): Promise<SupplierAnnouncementDetailDto> {
    const response = await fetch(
      `${this.baseUrl}/supplier/announcements/${encodeURIComponent(announcementId)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as SupplierAnnouncementDetailDto;
  }

  public async markRead(announcementId: string): Promise<SupplierAnnouncementReadAckDto> {
    const response = await fetch(
      `${this.baseUrl}/supplier/announcements/${encodeURIComponent(announcementId)}/read`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: this.mutationHeaders(),
      },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as SupplierAnnouncementReadAckDto;
  }

  /** En-têtes des MUTATIONS : content-type + double-submit CSRF (`x-csrf-token` = cookie `supplier_csrf`). */
  private mutationHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const csrf = readCsrfToken();
    if (csrf !== undefined) {
      headers['x-csrf-token'] = csrf;
    }
    return headers;
  }

  private async toApiError(response: Response): Promise<SupplierAnnouncementsApiError> {
    const body = await this.safeJson(response);
    const message =
      this.extractMessage(body) ?? `Erreur API (${String(response.status)}).`;
    const kind = STATUS_TO_KIND[response.status] ?? 'generic';
    return new SupplierAnnouncementsApiError(message, kind);
  }

  private extractMessage(body: NestErrorBody | undefined): string | undefined {
    const message = body?.message;
    if (message === undefined) {
      return undefined;
    }
    return typeof message === 'string' ? message : message.join(' ');
  }

  private async safeJson(response: Response): Promise<NestErrorBody | undefined> {
    try {
      return (await response.json()) as NestErrorBody;
    } catch {
      return undefined;
    }
  }
}
